import { TimeRangeEnum } from '../FilterSection/TimeRangeSelector/types';
import { KpiGranularity } from '../OvertimeAnalysis/types';
import { ChartData, ChartDataset, KpiDataItem } from './type';
import { ChartOptions } from 'chart.js';
import {
  PRIMARY_BAR_COLOR,
  PREVIOUS_BAR_COLOR,
  BAR_PERCENTAGE_SMALL,
  BAR_PERCENTAGE_MEDIUM,
  BAR_PERCENTAGE_LARGE,
  BAR_PERCENTAGE_XL,
  CATEGORY_PERCENTAGE_SMALL,
  CATEGORY_PERCENTAGE_MEDIUM,
  CATEGORY_PERCENTAGE_LARGE,
  CATEGORY_PERCENTAGE_XL,
  MAX_BAR_THICKNESS_SMALL,
  MAX_BAR_THICKNESS_MEDIUM,
  MAX_BAR_THICKNESS_LARGE,
  MAX_BAR_THICKNESS_XL,
} from './barChartConstants';

export function buildChartData(
  data: KpiDataItem[],
  timeRange: TimeRangeEnum,
  compareToPreviousPeriod: boolean,
  selectedMetric: string,
  granularity: KpiGranularity = KpiGranularity.Day
): ChartData {
  if (!data || data.length === 0) {
    return { labels: [], datasets: [] };
  }

  let currentYearLabels: string[] = [];
  let previousYearLabels: string[] = [];
  let labels: string[] = [];

  const isDailyRange = granularity === KpiGranularity.Day;
  const useMonthLabel =
    granularity === KpiGranularity.Month && timeRange !== TimeRangeEnum.Last30Days;

  const sortByDate = (a: KpiDataItem, b: KpiDataItem) =>
    new Date(`${a.kpi_label}T00:00:00Z`).getTime() - new Date(`${b.kpi_label}T00:00:00Z`).getTime();
  const monthLabelFor = (label: string) =>
    useMonthLabel
      ? new Date(label).toLocaleString('default', { month: 'long' })
      : label.slice(0, 7);
  const labelFor = (label: string) =>
    granularity === KpiGranularity.Month ? monthLabelFor(label) : label;
  const addUtcDays = (label: string, days: number) => {
    const date = new Date(`${label}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return label;
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  };

  const currentData = data.filter(item => item.period_type === 'current').sort(sortByDate);
  const previousData = data.filter(item => item.period_type === 'previous').sort(sortByDate);
  const currentDataByDate = new Map(currentData.map(item => [item.kpi_label, item]));
  const previousDataByDate = new Map(previousData.map(item => [item.kpi_label, item]));
  const shouldUseLast30DayScaffold =
    compareToPreviousPeriod &&
    timeRange === TimeRangeEnum.Last30Days &&
    granularity === KpiGranularity.Day &&
    currentData.length > 0;
  const last30DayCurrentLabels = shouldUseLast30DayScaffold
    ? Array.from({ length: 30 }, (_, index) => addUtcDays(currentData[0].kpi_label, index))
    : [];
  const last30DayPreviousLabels = shouldUseLast30DayScaffold
    ? last30DayCurrentLabels.map(label => addUtcDays(label, -30))
    : [];

  if (shouldUseLast30DayScaffold) {
    labels = last30DayCurrentLabels;
    currentYearLabels = last30DayCurrentLabels;
    previousYearLabels = last30DayPreviousLabels;
  } else if (compareToPreviousPeriod) {
    const axisSource = currentData.length ? currentData : previousData;
    labels = axisSource.map(item => labelFor(item.kpi_label));
    currentYearLabels = currentData.map(item => labelFor(item.kpi_label));
    previousYearLabels = previousData.map(item => labelFor(item.kpi_label));
  } else if (isDailyRange) {
    currentYearLabels = [...new Set(currentData.map(item => item.kpi_label))];
    labels = currentYearLabels;
    previousYearLabels = [...new Set(previousData.map(item => item.kpi_label))];
  } else {
    currentYearLabels = [...new Set(currentData.map(item => labelFor(item.kpi_label)))];
    previousYearLabels = [...new Set(previousData.map(item => labelFor(item.kpi_label)))];
    labels = Array.from(new Set([...currentYearLabels, ...previousYearLabels]));
  }

  if (isDailyRange) {
    labels.sort((a, b) => a.localeCompare(b));
  }

  const currentLabelMap = new Map(currentData.map(i => [labelFor(i.kpi_label), i]));
  const previousLabelMap = new Map(previousData.map(i => [labelFor(i.kpi_label), i]));

  if (!compareToPreviousPeriod && !isDailyRange) {
    labels.sort((a, b) => {
      const aItem = currentLabelMap.get(a) || previousLabelMap.get(a);
      const bItem = currentLabelMap.get(b) || previousLabelMap.get(b);
      const aDate = aItem ? new Date(`${aItem.kpi_label}T00:00:00Z`) : null;
      const bDate = bItem ? new Date(`${bItem.kpi_label}T00:00:00Z`) : null;
      if (aDate && bDate) return aDate.getTime() - bDate.getTime();
      if (aDate) return -1;
      if (bDate) return 1;
      return a.localeCompare(b);
    });
  }

  const currentLabel = compareToPreviousPeriod ? 'Current period' : 'Current';
  const previousLabel = compareToPreviousPeriod ? 'Previous period' : 'Previous';

  const currentDataMap = new Map(currentData.map(i => [i.kpi_label, i]));
  const previousDataMap = new Map(previousData.map(i => [i.kpi_label, i]));

  const currentRawLabels = labels.map((label, index) => {
    if (shouldUseLast30DayScaffold) {
      return currentDataByDate.has(label) ? label : null;
    }
    if (compareToPreviousPeriod) {
      return currentData[index]?.kpi_label || null;
    }
    if (isDailyRange) {
      return label;
    }
    const item = currentLabelMap.get(label);
    return item ? item.kpi_label : null;
  });

  const previousRawLabels = labels.map((label, index) => {
    if (shouldUseLast30DayScaffold) {
      const rawPreviousLabel = last30DayPreviousLabels[index];
      return previousDataByDate.has(rawPreviousLabel) ? rawPreviousLabel : null;
    }
    if (compareToPreviousPeriod) {
      return previousData[index]?.kpi_label || null;
    }
    if (isDailyRange) {
      const labelDate = new Date(label);
      const prevLabel = new Date(labelDate);
      if (timeRange === TimeRangeEnum.Last1Month) {
        prevLabel.setUTCMonth(labelDate.getUTCMonth() - 1);
      } else if (timeRange === TimeRangeEnum.Last30Days) {
        prevLabel.setUTCDate(labelDate.getUTCDate() - 30);
      } else {
        const shiftDays = timeRange === TimeRangeEnum.Last1Day ? 1 : 7;
        prevLabel.setUTCDate(labelDate.getUTCDate() - shiftDays);
      }
      const prevLabelStr = prevLabel.toISOString().slice(0, 10);
      const item = previousDataMap.get(prevLabelStr);
      return item ? item.kpi_label : null;
    }
    const item = previousLabelMap.get(label);
    return item ? item.kpi_label : null;
  });

  const barCount = labels.length;
  let barPercentage = 0.7;
  let categoryPercentage = 0.8;
  let maxBarThickness: number | undefined = undefined;

  if (barCount <= 5) {
    barPercentage = BAR_PERCENTAGE_SMALL;
    categoryPercentage = CATEGORY_PERCENTAGE_SMALL;
    maxBarThickness = MAX_BAR_THICKNESS_SMALL;
  } else if (barCount <= 10) {
    barPercentage = BAR_PERCENTAGE_MEDIUM;
    categoryPercentage = CATEGORY_PERCENTAGE_MEDIUM;
    maxBarThickness = MAX_BAR_THICKNESS_MEDIUM;
  } else if (barCount <= 20) {
    barPercentage = BAR_PERCENTAGE_LARGE;
    categoryPercentage = CATEGORY_PERCENTAGE_LARGE;
    maxBarThickness = MAX_BAR_THICKNESS_LARGE;
  } else {
    barPercentage = BAR_PERCENTAGE_XL;
    categoryPercentage = CATEGORY_PERCENTAGE_XL;
    maxBarThickness = MAX_BAR_THICKNESS_XL;
  }

  if (compareToPreviousPeriod && barCount <= 2) {
    barPercentage = 1.0;
    categoryPercentage = 1.0;
  }
  const forceWideBars = compareToPreviousPeriod && barCount <= 2;
  const barThickness = forceWideBars ? 120 : undefined;

  const datasets: ChartDataset[] = [];
  if (compareToPreviousPeriod) {
    datasets.push({
      label: previousLabel,
      data: labels.map((label, idx) => {
        const previousItem = shouldUseLast30DayScaffold
          ? previousDataByDate.get(last30DayPreviousLabels[idx])
          : previousData[idx];
        const value: number | null = previousItem ? Number(previousItem.value) : null;
        if ((selectedMetric === 'ctr' || selectedMetric === 'cvr') && value !== null) {
          return value * 100;
        }
        return value;
      }),
      backgroundColor: PREVIOUS_BAR_COLOR,
      borderRadius: 0,
      barPercentage,
      categoryPercentage,
      ...(barThickness ? { barThickness, maxBarThickness: barThickness } : {}),
      ...(maxBarThickness ? { maxBarThickness } : {}),
      labels: previousYearLabels,
      rawLabels: previousRawLabels,
    } as any);
  }
  datasets.push({
    label: currentLabel,
    data: labels.map((label, idx) => {
      let value: number | null;
      if (shouldUseLast30DayScaffold) {
        const item = currentDataByDate.get(label);
        value = item ? Number(item.value) : null;
      } else if (compareToPreviousPeriod) {
        value = currentData[idx] ? Number(currentData[idx].value) : null;
      } else if (isDailyRange) {
        const item = currentDataMap.get(label);
        value = item ? Number(item.value) : null;
      } else {
        const item = currentLabelMap.get(label);
        value = item ? Number(item.value) : null;
      }
      if ((selectedMetric === 'ctr' || selectedMetric === 'cvr') && value !== null) {
        return value * 100;
      }
      return value;
    }),
    backgroundColor: PRIMARY_BAR_COLOR,
    borderRadius: 0,
    barPercentage,
    categoryPercentage,
    ...(barThickness ? { barThickness, maxBarThickness: barThickness } : {}),
    ...(maxBarThickness ? { maxBarThickness } : {}),
    labels: currentYearLabels,
    rawLabels: currentRawLabels,
  } as any);

  return {
    labels: labels,
    datasets: datasets,
  };
}

export function generateBarOptions(
  chartData: ChartData,
  selectedMetric: string,
  granularity: KpiGranularity,
  timeRange: TimeRangeEnum
): ChartOptions<'bar'> {
  let max = 0;
  let min = Number.POSITIVE_INFINITY;
  chartData.datasets.forEach(ds => {
    ds.data.forEach((val: any) => {
      const num = Number(val);
      if (!isNaN(num) && val !== null) {
        if (num > max) max = num;
        if (num < min) min = num;
      }
    });
  });

  function niceNumber(range: number, round: boolean) {
    const exponent = Math.floor(Math.log10(range));
    const fraction = range / Math.pow(10, exponent);
    let niceFraction;
    if (round) {
      if (fraction < 1.5) niceFraction = 1;
      else if (fraction < 3) niceFraction = 2;
      else if (fraction < 7) niceFraction = 5;
      else niceFraction = 10;
    } else {
      if (fraction <= 1) niceFraction = 1;
      else if (fraction <= 2) niceFraction = 2;
      else if (fraction <= 5) niceFraction = 5;
      else niceFraction = 10;
    }
    return niceFraction * Math.pow(10, exponent);
  }

  const yMin = 0;
  const range = max - yMin;
  const niceStep = niceNumber(range / 5, true);
  const yMax = Math.ceil(max / niceStep) * niceStep;
  const stepSize = niceStep;
  const precision = stepSize < 1 && stepSize > 0 ? Math.abs(Math.floor(Math.log10(stepSize))) : 0;
  const safePrecision = Math.max(0, Math.min(20, isFinite(precision) ? Math.round(precision) : 0));

  const formatNumber = (v: number) => {
    if (v === 0) return '0';
    return v.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: safePrecision,
    });
  };

  const formatRangeLabel = (label: string) => {
    if (!label || !/^\d{4}-\d{2}-\d{2}$/.test(label)) return label;
    const start = new Date(`${label}T00:00:00Z`);
    if (Number.isNaN(start.getTime())) return label;
    if (granularity === KpiGranularity.Week) {
      const end = new Date(start);
      end.setUTCDate(start.getUTCDate() + 6);
      return `${label} - ${end.toISOString().slice(0, 10)}`;
    }
    if (granularity === KpiGranularity.Month) {
      if (timeRange === TimeRangeEnum.Last30Days) {
        const end = new Date(start);
        end.setUTCDate(start.getUTCDate() + 29);
        return `${label} - ${end.toISOString().slice(0, 10)}`;
      }
      const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0));
      return `${label} - ${end.toISOString().slice(0, 10)}`;
    }
    return label;
  };

  const tickStep = (() => {
    const labelCount = chartData.labels.length;
    if (granularity !== KpiGranularity.Day) return 1;
    if (labelCount > 50) return 5;
    if (labelCount > 30) return 3;
    if (labelCount > 20) return 2;
    return 1;
  })();

  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { font: { size: 14 }, boxWidth: 7, boxHeight: 7 },
      },
      title: { display: false },
      tooltip: {
        enabled: false,
        external: function (context) {
          let tooltipEl = document.getElementById('chartjs-custom-tooltip');
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-custom-tooltip';
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.background = 'rgba(0,0,0,0.7)';
            tooltipEl.style.border = 'none';
            tooltipEl.style.borderRadius = '6px';
            tooltipEl.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.transition = 'all .1s ease';
            tooltipEl.style.padding = '9px 12px';
            tooltipEl.style.zIndex = '1000';
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
          }

          tooltipEl.classList.remove('above', 'below', 'no-transform');
          if (tooltipModel.yAlign) {
            tooltipEl.classList.add(tooltipModel.yAlign);
          } else {
            tooltipEl.classList.add('no-transform');
          }

          if (tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0) {
            const dp = tooltipModel.dataPoints[0];
            const label = dp.dataset.label;
            const rawValue = typeof dp.raw === 'number' ? dp.raw : Number(dp.raw);
            let value: string;
            if (selectedMetric === 'ctr' || selectedMetric === 'cvr') {
              value =
                rawValue.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                }) + '%';
            } else if (selectedMetric === 'syte_revenue') {
              value =
                '$' +
                rawValue.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                });
            } else if (selectedMetric === 'aov' || selectedMetric === 'arpu') {
              value =
                '$' +
                rawValue.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                });
            } else {
              value = rawValue.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              });
            }
            const rawLabels = (dp.dataset as any).rawLabels;
            let kpiLabel = rawLabels && rawLabels[dp.dataIndex] ? rawLabels[dp.dataIndex] : null;
            let barColor = dp.dataset.backgroundColor || PRIMARY_BAR_COLOR;
            if (Array.isArray(barColor)) {
              barColor = barColor[dp.dataIndex] || barColor[0] || PRIMARY_BAR_COLOR;
            }

            if (kpiLabel && typeof kpiLabel === 'string') {
              kpiLabel = formatRangeLabel(kpiLabel);
            }

            tooltipEl.innerHTML = `
              <div style="font-weight: 600; font-size: 11.25px; color: #fff; margin-bottom: 1.5px;">${label}</div>
              <div style="font-size: 13.5px; color: #fff; margin-bottom: ${kpiLabel ? '6px' : '0'};">${value}</div>
              ${
                kpiLabel
                  ? `<div style="border-top: 1px solid rgba(255,255,255,0.15); margin: 4.5px 0 3px 0;"></div>
              <div style="font-size: 9.75px; color: #fff;">${kpiLabel}</div>`
                  : ''
              }
            `;
          }

          const { chart } = context;
          const position = chart.canvas.getBoundingClientRect();
          tooltipEl.style.opacity = '1';
          tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
          tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: '#E0E0E0',
          ...({ borderDash: [5, 5] } as any),
        },
        ticks: {
          font: { size: 12 },
          callback: (_value, index) => {
            if (granularity !== KpiGranularity.Day) return chartData.labels[index] || '';
            return index % tickStep === 0 ? chartData.labels[index] || '' : '';
          },
        },
      },
      y: {
        min: yMin,
        max: yMax,
        ticks: {
          callback: (v: number) => formatNumber(v),
          font: { size: 12 },
          precision,
          stepSize,
        },
        grid: {
          display: true,
          color: '#E0E0E0',
          ...({ borderDash: [5, 5] } as any),
        },
      },
    },
  };
}
